import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { styled, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { getEndpointByDasId } from '../../../apis/devices/CommonDevice';

import { handleIconMedium } from '../../../assets/SvgIcon/HandleIcon';

const Container = styled('div')`
  width: 100%;
  background: rgba(0, 0, 0, 0.2);
  margin-bottom: 10px;
  border-radius: 4px;
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  height: 32px;
  color: white;
  padding-left: 10px;
`;

const Content = styled('div')`
  border-radius: 4px;
  padding: 10px;
`;

const BindToDevice = styled('div')`
  background: rgba(0, 0, 0, 0.1);
  height: 64px;
  padding: 12px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color.box_bbg};
`;

const Title = styled(Typography)(({ theme }) => ({
  ...theme.externalTypography.body3,
  color: theme.color.secondary.$60,
  height: '16px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  marginTop: '8px',
}));

const Text = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.color.box_bbg,
  height: '24px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

interface DasWBGTConnectedDeviceCardViewProps {
  index: number;
  dasId: string;
  projectId: string | undefined;
}

const DasWBGTConnectedDeviceCardView: React.FC<
  DasWBGTConnectedDeviceCardViewProps
> = ({ index, dasId, projectId }) => {
  const { projectId: routeProjectId } = useParams();
  const resolvedProjectId = projectId ?? routeProjectId;
  const { t } = useTranslation();

  const { data: endpointRes } = useQuery({
    queryKey: ['daswbgt-endpoint-view', resolvedProjectId, dasId],
    queryFn: () =>
      getEndpointByDasId({
        projectId: resolvedProjectId as string,
        dasId,
      }),
    enabled: !!resolvedProjectId && !!dasId,
    refetchOnWindowFocus: false,
  });

  const endpoint = endpointRes?.data.data;

  const locationText =
    endpoint?.coordinates?.lon && endpoint?.coordinates?.lat
      ? `${endpoint.coordinates.lon}, ${endpoint.coordinates.lat}`
      : 'N/A';

  const altitudeText =
    endpoint?.coordinates?.alt != null
      ? `${endpoint.coordinates.alt} M`
      : 'N/A';

  return (
    <Container>
      <Header>
        <Typography variant="body2">
          {`${t('project-settingJson.page.plant-management.dialog.create-plant.connected-device.title')} ${index + 1}`}
        </Typography>
      </Header>
      <Content>
        <BindToDevice>
          {handleIconMedium('daswbgt')}
          <Typography variant="body2" sx={{ ml: '9px' }}>
            {dasId}
          </Typography>
        </BindToDevice>

        <Title>{t('project-settingJson.page.devices.daswbgt.name')}</Title>
        <Text title={endpoint?.name ?? 'N/A'}>{endpoint?.name ?? 'N/A'}</Text>

        <Title>{t('location')}</Title>
        <Text title={locationText}>{locationText}</Text>

        <Title>{t('altitude')}</Title>
        <Text title={altitudeText}>{altitudeText}</Text>

        <Title>
          {t('project-settingJson.page.devices.daswbgt.default-wbgt')}
        </Title>
        <Text>{endpoint?.isDefaultWbgt ? t('yes') : t('no')}</Text>
      </Content>
    </Container>
  );
};

export default DasWBGTConnectedDeviceCardView;
